// Task tools BossDrop can write to: their saved-prefs shapes, the rule that
// makes each one usable, and the order BossDrop falls back through.
//
// The prefs shapes mirror the server's sanitizers (sanitizeClickUpPrefs,
// sanitizeNotionPrefs and sanitizeMondayPrefs in backend/server/*Client.js),
// so a prefs round trip compares equal and the connector autosaves do not
// loop. Every reader on the server re-sanitizes; these only keep the hub from
// saving a value the server would change.

// planner > asana > jira > linear > clickup > notion > monday, the same order
// as pickTaskBackend in backend/server/automationWorker.js and
// backend/server/brain/executePlan.js. New tools go LAST, so a workspace that
// already had a task tool keeps writing to it.
export const TASK_TOOL_ORDER = [
  "planner",
  "asana",
  "jira",
  "linear",
  "clickup",
  "notion",
  "monday",
];

export const TASK_TOOL_LABELS = {
  planner: "Microsoft Planner",
  asana: "Asana",
  jira: "Jira",
  linear: "Linear",
  clickup: "ClickUp",
  notion: "Notion",
  monday: "monday.com",
};

// The tool BossDrop writes new tasks to: the one asked for when it is usable,
// otherwise the first usable one in TASK_TOOL_ORDER, otherwise null.
// `ready` is { planner: bool, asana: bool, ... }.
export function pickTaskBackend(want, ready = {}) {
  const w = String(want || "").toLowerCase();
  if (TASK_TOOL_ORDER.includes(w) && ready[w]) return w;
  return TASK_TOOL_ORDER.find((key) => !!ready[key]) || null;
}

export const PM_DEFAULT_LOOKBACK_DAYS = 60;

// 1..365 whole days; anything else is the default (the servers' clampLookback).
function lookbackDays(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 1) return PM_DEFAULT_LOOKBACK_DAYS;
  return Math.min(365, n);
}

function plainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}

// Unique ids in the order given, each passed through `canon` and kept only
// when it matches `re`, at most `max`.
function idList(value, re, max, canon = (id) => id) {
  const out = [];
  for (const raw of Array.isArray(value) ? value : []) {
    const id = canon(String(raw ?? "").trim());
    if (!id || !re.test(id) || out.includes(id)) continue;
    out.push(id);
    if (out.length >= max) break;
  }
  return out;
}

/* ---------- ClickUp ---------- */
// Workspace ("team") and list ids are numeric strings. They are always sent as
// STRINGS: a large id sent as a JSON number can lose precision and then fail
// the server's allowlist without a sound.
const CLICKUP_ID_RE = /^\d{1,24}$/;
export const MAX_CLICKUP_LISTS = 50;

export function clickupPrefsShape(prefs) {
  const src = plainObject(prefs);
  const teamId = String(src.teamId ?? "").trim();
  const listIds = idList(src.listIds, CLICKUP_ID_RE, MAX_CLICKUP_LISTS);
  const defaultListId = String(src.defaultListId ?? "").trim();
  return {
    teamId: CLICKUP_ID_RE.test(teamId) ? teamId : "",
    listIds,
    // New tasks land in a list BossDrop also reads back.
    defaultListId: listIds.includes(defaultListId) ? defaultListId : "",
    lookbackDays: lookbackDays(src.lookbackDays),
  };
}

// isClickUpPrefsReady on the server: a ClickUp workspace, a list to read and
// a default list (one of them) for new tasks.
export function clickupToolReady(connected, prefs) {
  const p = clickupPrefsShape(prefs);
  return !!(connected && p.teamId && p.listIds.length && p.defaultListId);
}

/* ---------- Notion ---------- */
export const MAX_NOTION_DATA_SOURCES = 20;
const NOTION_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const NOTION_STATUS_TYPES = ["status", "select", "checkbox"];
const NOTION_KEY_PREFIX_RE = /^[A-Z][A-Z0-9]{0,9}$/;
const MAX_OPTION_NAMES = 20;

// Canonical lower-case dashed UUID from the dashed or 32-hex form, else ""
// (normalizeNotionId on the server).
export function notionIdShape(value) {
  const hex = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "");
  if (!/^[0-9a-f]{32}$/.test(hex)) return "";
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// Notion property ids are short URL-encoded strings ("title", "%3AUPp").
function notionPropertyId(value) {
  const text = String(value ?? "").trim();
  return /^[\x21-\x7e]{1,64}$/.test(text) ? text : "";
}

function optionName(value, max = 100) {
  const text = String(value ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .trim();
  return text && text.length <= max ? text : "";
}

function optionNameList(value) {
  return Array.from(
    new Set((Array.isArray(value) ? value : []).map((v) => optionName(v)).filter(Boolean)),
  ).slice(0, MAX_OPTION_NAMES);
}

// One data source's column mapping, every field present, in the server's key
// order. Property IDs, not names; "" means none.
export function notionMapShape(value) {
  const src = plainObject(value);
  const statusType = String(src.statusType || "").trim();
  const keyPrefix = String(src.keyPrefix ?? "")
    .trim()
    .toUpperCase();
  return {
    title: notionPropertyId(src.title),
    status: notionPropertyId(src.status),
    statusType: NOTION_STATUS_TYPES.includes(statusType) ? statusType : "",
    doneOptions: optionNameList(src.doneOptions),
    inProgressOption: optionName(src.inProgressOption),
    blockedOptions: optionNameList(src.blockedOptions),
    assignee: notionPropertyId(src.assignee),
    due: notionPropertyId(src.due),
    priority: notionPropertyId(src.priority),
    blockedBy: notionPropertyId(src.blockedBy),
    key: notionPropertyId(src.key),
    keyPrefix: NOTION_KEY_PREFIX_RE.test(keyPrefix) ? keyPrefix : "",
    notes: notionPropertyId(src.notes),
  };
}

export function notionPrefsShape(prefs) {
  const src = plainObject(prefs);
  const dataSourceIds = idList(
    src.dataSourceIds,
    NOTION_ID_RE,
    MAX_NOTION_DATA_SOURCES,
    notionIdShape,
  );
  const defaultDataSourceId = notionIdShape(src.defaultDataSourceId);
  const srcMaps = plainObject(src.propertyMaps);
  const byId = new Map();
  for (const [rawId, map] of Object.entries(srcMaps)) {
    const id = notionIdShape(rawId);
    if (id && !byId.has(id)) byId.set(id, map);
  }
  // Only chosen data sources keep a map, in the order they were chosen. A data
  // source with no saved map uses the server's auto-map.
  const propertyMaps = {};
  for (const id of dataSourceIds) {
    if (byId.has(id)) propertyMaps[id] = notionMapShape(byId.get(id));
  }
  return {
    dataSourceIds,
    defaultDataSourceId: dataSourceIds.includes(defaultDataSourceId)
      ? defaultDataSourceId
      : "",
    propertyMaps,
    lookbackDays: lookbackDays(src.lookbackDays),
  };
}

// isNotionTaskToolReady on the server: a database to read and a default
// database (one of them) for new tasks.
export function notionToolReady(connected, prefs) {
  const p = notionPrefsShape(prefs);
  return !!(connected && p.dataSourceIds.length && p.defaultDataSourceId);
}

/* ---------- monday.com ---------- */
// Same cap as MAX_MONDAY_BOARDS in backend/server/mondayClient.js, which sizes
// it to monday.com's daily API call budget.
export const MAX_MONDAY_BOARDS = 10;
const MONDAY_BOARD_ID_RE = /^\d{1,20}$/;
const MONDAY_COLUMN_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const MAX_MONDAY_LABELS = 20;

function mondayColumnId(value) {
  const text = String(value ?? "").trim();
  return MONDAY_COLUMN_ID_RE.test(text) ? text : "";
}

function mondayLabel(value) {
  const text = String(value ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text && text.length <= 64 ? text : "";
}

function mondayLabelList(value) {
  const seen = new Set();
  const out = [];
  for (const raw of Array.isArray(value) ? value : []) {
    const label = mondayLabel(raw);
    if (!label || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    out.push(label);
    if (out.length >= MAX_MONDAY_LABELS) break;
  }
  return out;
}

// One board's column mapping in the server's key order. "" and [] mean
// "pick automatically"; a map with nothing set is dropped (null), as the
// server drops it.
export function mondayMapShape(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const map = {
    status: mondayColumnId(value.status),
    doneLabels: mondayLabelList(value.doneLabels),
    inProgressLabel: mondayLabel(value.inProgressLabel),
    blockedLabels: mondayLabelList(value.blockedLabels),
    people: mondayColumnId(value.people),
    date: mondayColumnId(value.date),
    priority: mondayColumnId(value.priority),
    dependency: mondayColumnId(value.dependency),
    notes: mondayColumnId(value.notes),
  };
  const empty = Object.values(map).every((v) =>
    Array.isArray(v) ? !v.length : !v,
  );
  return empty ? null : map;
}

export function mondayPrefsShape(prefs) {
  const src = plainObject(prefs);
  const boardIds = idList(src.boardIds, MONDAY_BOARD_ID_RE, MAX_MONDAY_BOARDS);
  const defaultBoardIdRaw = String(src.defaultBoardId ?? "").trim();
  const defaultBoardId = boardIds.includes(defaultBoardIdRaw)
    ? defaultBoardIdRaw
    : "";
  const groupId = String(src.defaultGroupId ?? "").trim();
  const srcMaps = plainObject(src.columnMaps);
  const columnMaps = {};
  for (const boardId of boardIds) {
    if (!Object.prototype.hasOwnProperty.call(srcMaps, boardId)) continue;
    const map = mondayMapShape(srcMaps[boardId]);
    if (map) columnMaps[boardId] = map;
  }
  return {
    boardIds,
    defaultBoardId,
    // A group only means something on the default board.
    defaultGroupId:
      defaultBoardId && MONDAY_COLUMN_ID_RE.test(groupId) ? groupId : "",
    columnMaps,
    lookbackDays: lookbackDays(src.lookbackDays),
  };
}

// isMondayTaskToolReady on the server: a board to read and a default board
// (one of them) for new items.
export function mondayToolReady(connected, prefs) {
  const p = mondayPrefsShape(prefs);
  return !!(connected && p.boardIds.length && p.defaultBoardId);
}

/* ---------- Planner, Asana, Jira and Linear ---------- */
// The gates the worker applies before it writes a task to these four.

// Planner hangs off the Microsoft connection: a tenant and team, and the plan
// new tasks go into.
export function plannerToolReady(connected, teamsPrefs) {
  const p = plainObject(teamsPrefs);
  return !!(connected && p.tenantId && p.teamId && p.planId);
}

export function asanaToolReady(connected, asanaPrefs) {
  return !!(connected && plainObject(asanaPrefs).workspace_gid);
}

export function jiraToolReady(connected, jiraPrefs) {
  const p = plainObject(jiraPrefs);
  return !!(connected && p.siteId && (p.projectId || p.projectKey));
}

// The worker needs a team to read, and createLinearIssue a default team.
export function linearToolReady(connected, linearPrefs) {
  const p = plainObject(linearPrefs);
  return !!(connected && p.teamIds?.length && p.defaultTeamId);
}

/* ---------- Every task tool's state ---------- */
// What is left to set up on a CONNECTED tool that is not ready yet, in the
// words its connector card uses. `prefs` holds each tool's saved prefs.
function taskToolSetupStep(key, prefs) {
  switch (key) {
    case "planner": {
      const p = plainObject(prefs.teamsPrefs);
      return p.tenantId && p.teamId ? "pick a plan" : "pick a team and a plan";
    }
    case "asana":
      return "pick a workspace";
    case "jira":
      return plainObject(prefs.jiraPrefs).siteId ? "pick a project" : "pick a site";
    case "linear":
      return plainObject(prefs.linearPrefs).teamIds?.length
        ? "pick a default team for new issues"
        : "pick a team to read";
    case "clickup": {
      const p = clickupPrefsShape(prefs.clickupPrefs);
      if (!p.teamId) return "pick a workspace";
      if (!p.listIds.length) return "pick a list to read";
      return "pick a default list for new tasks";
    }
    case "notion":
      return notionPrefsShape(prefs.notionPrefs).dataSourceIds.length
        ? "pick a default database for new tasks"
        : "pick a database to read";
    case "monday":
      return mondayPrefsShape(prefs.mondayPrefs).boardIds.length
        ? "pick a default board for new items"
        : "pick a board to read";
    default:
      return "";
  }
}

/**
 * Every task tool in TASK_TOOL_ORDER as { key, label, connected, ready, step }.
 * `connected` is each tool's raw connection ({ planner, asana, ... }; Planner's
 * is the Microsoft connection), `prefs` its saved prefs ({ teamsPrefs,
 * asanaPrefs, jiraPrefs, linearPrefs, clickupPrefs, notionPrefs, mondayPrefs }).
 * `ready` is the gate above; `step` says what to finish on a connected tool
 * that is not ready, and is "" otherwise.
 */
export function taskToolStates(connected = {}, prefs = {}) {
  const c = plainObject(connected);
  const p = plainObject(prefs);
  const ready = {
    planner: plannerToolReady(c.planner, p.teamsPrefs),
    asana: asanaToolReady(c.asana, p.asanaPrefs),
    jira: jiraToolReady(c.jira, p.jiraPrefs),
    linear: linearToolReady(c.linear, p.linearPrefs),
    clickup: clickupToolReady(c.clickup, p.clickupPrefs),
    notion: notionToolReady(c.notion, p.notionPrefs),
    monday: mondayToolReady(c.monday, p.mondayPrefs),
  };
  return TASK_TOOL_ORDER.map((key) => {
    const isConnected = !!c[key];
    return {
      key,
      label: TASK_TOOL_LABELS[key],
      connected: isConnected,
      ready: ready[key],
      step: isConnected && !ready[key] ? taskToolSetupStep(key, p) : "",
    };
  });
}
